import { memo, useCallback, useMemo, useState } from 'react';
import { Focusable, Key } from 'react-aria-components';

import {
  Button,
  Flex,
  Label,
  PillSelectValue,
  Radio,
  RadioGroup,
  SelectPills,
  SelectPillsOption,
  Separator,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { findField, FormatterValidatorHeader, ValidationFormSettings } from '@/components/ui';
import { useDebounceCallback, useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';
import { getCountryByLocale, Id, isArray } from '@/utils';

const SUPPORTED_LOCALES = [
  'bg-BG',
  'cs-CZ',
  'de-AT',
  'de-DE',
  'dk-DK',
  'el-CY',
  'el-GR',
  'en-CA',
  'en-GB',
  'en-IE',
  'en-US',
  'es-AR',
  'es-ES',
  'et-EE',
  'fi-FI',
  'fr-BE',
  'fr-CA',
  'fr-FR',
  'fr-LU',
  'hr-HR',
  'hu-HU',
  'it-IT',
  'lb-LU',
  'lt-LT',
  'lv-LV',
  'mt-MT',
  'nl-BE',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ro-RO',
  'sk-SK',
  'sl-SI',
  'sv-SE',
  'uk-UA',
];

export type TaxIdValidatorProps = {
  fieldId: string;
  validatorId: string;
};

const ITaxIdValidator = ({ fieldId, validatorId }: TaxIdValidatorProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const validator = field?.validators?.find((v) => v.id === validatorId);

  const [isSpecific, setIsSpecific] = useState(() => {
    if (isArray(validator?.config.locales) && validator.config.locales.length > 0) {
      return true;
    }
    return false;
  });

  const onSetLocales = useDebounceCallback(
    useCallback((locales: string[] | undefined) => mutate((f) => (f.config.locales = locales)), [mutate]),
    500
  );

  const buttonComponent = (
    <Button>
      <PillSelectValue>
        {(item?: Id | null) => {
          return item ? (
            <TooltipTrigger>
              <Tooltip variant="primary">
                <Text>{getCountryByLocale(item.id)?.name}</Text>
              </Tooltip>

              <Focusable>
                <Text>{getCountryByLocale(item.id)?.emoji}</Text>
              </Focusable>
            </TooltipTrigger>
          ) : null;
        }}
      </PillSelectValue>
      <ChevronIcon rotation="down" />
    </Button>
  );

  const onChangeSpecific = useCallback(
    (key: Key) => {
      setIsSpecific(key.toString() === 'specific');
      onSetLocales(undefined);
    },
    [onSetLocales]
  );

  const locales = useMemo(() => {
    return SUPPORTED_LOCALES.toSorted((a, b) => {
      const nameA = getCountryByLocale(a)?.name ?? '';
      const nameB = getCountryByLocale(b)?.name ?? '';
      return nameA.localeCompare(nameB);
    }).map((code) => ({ id: code }));
  }, []);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader validator={validator} />
      <Separator orientation="horizontal" />

      <Flex col className="gap-sm">
        <Label>Locales</Label>

        <RadioGroup onChange={onChangeSpecific} defaultValue={isSpecific ? 'specific' : 'all'}>
          <Radio value="all">
            <Text slot="description">All supported locales</Text>
          </Radio>
          <Radio value="specific">
            <Text slot="description">Select specific locales</Text>
          </Radio>
        </RadioGroup>

        {isSpecific ? (
          <SelectPills<Id>
            value={validator?.config.locales as string[] | undefined}
            onChange={(locales) => onSetLocales(locales.map((c) => c.toString()))}
            optionRenderFn={(option) => (
              <SelectPillsOption value={option}>
                {option ? (
                  <div className="gap-sm flex items-center">
                    <Text>{getCountryByLocale(option.id)?.emoji}</Text>
                    <Text>{getCountryByLocale(option.id)?.name}</Text>
                  </div>
                ) : null}
              </SelectPillsOption>
            )}
            items={locales}
            buttonComponent={buttonComponent}
          />
        ) : null}
      </Flex>

      <ValidationFormSettings fieldId={fieldId} validatorId={validatorId} />
    </div>
  );
};

export const TaxIdValidator = memo(ITaxIdValidator) as typeof ITaxIdValidator;
